import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toPng } from "html-to-image";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { Trophy, DownloadSimple, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function InstitutionReport() {
  const { id } = useParams();
  const now = new Date().getFullYear();
  const [year, setYear] = useState(String(now));
  const [r, setR] = useState(null);
  const ref = useRef(null);
  useSeo("Alumni Impact Report", "A shareable yearly report of alumni impact.");
  useEffect(() => {
    const q = year === "all" ? "" : `?year=${year}`;
    api.get(`/institutions/${id}/impact-report${q}`).then((res) => setR(res.data)).catch(() => setR(false));
  }, [id, year]);

  const download = async () => {
    if (!ref.current) return;
    try {
      const dataUrl = await toPng(ref.current, { cacheBust: true, pixelRatio: 2, skipFonts: true, backgroundColor: "#ffffff" });
      const a = document.createElement("a"); a.download = `AlumniPulse-Impact-${year}.png`; a.href = dataUrl; a.click();
      toast.success("Report downloaded");
    } catch { toast.error("Could not generate the image"); }
  };

  if (r === false) return <div className="mx-auto max-w-3xl px-4 py-20 text-center text-muted-foreground">Report unavailable.</div>;
  if (!r) return <div className="mx-auto max-w-3xl px-4 py-20 text-center text-muted-foreground">Loading…</div>;
  const H = r.highlights;
  const stat = [
    ["Alumni participated", H.alumni_participated], ["Verified contributions", H.verified_contributions],
    ["Scholarships & student sponsorships", H.scholarships], ["Students mentored", H.students_mentored],
    ["Internships created", H.internships], ["Jobs offered", H.jobs_offered],
    ["Technology & equipment gifts", H.technology_donations], ["Books donated", H.books_donated],
    ["Research supported", H.research_supported], ["Projects completed", H.projects_completed],
  ];

  return (
    <div className="mx-auto max-w-3xl px-4 py-8" data-testid="institution-report">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Link to={`/institutions/${id}`} className="inline-flex items-center gap-1.5 text-sm text-secondary hover:underline"><ArrowLeft size={15} /> Back to institution</Link>
        <div className="flex items-center gap-2">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-32" data-testid="report-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[now, now - 1, now - 2, now - 3].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              <SelectItem value="all">All-time</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={download} className="gap-1.5" data-testid="report-download"><DownloadSimple size={16} /> Download</Button>
        </div>
      </div>

      <div ref={ref} className="overflow-hidden rounded-2xl border border-secondary/30 bg-gradient-to-br from-green-50 via-white to-orange-50 p-8">
        <div className="flex items-center gap-2 text-secondary"><Trophy size={22} weight="duotone" /><span className="text-[11px] font-semibold uppercase tracking-[0.2em]">AlumniPulse Impact Report</span></div>
        <h1 className="mt-3 font-heading text-3xl font-medium">{r.institution.name}</h1>
        <div className="text-sm text-muted-foreground">{r.institution.location} · Impact for {String(r.year)}</div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {stat.filter(([, v]) => v > 0).length === 0 ? (
            <div className="col-span-full text-sm text-muted-foreground">No verified alumni contributions recorded for this period yet.</div>
          ) : stat.filter(([, v]) => v > 0).map(([label, v]) => (
            <div key={label} className="rounded-xl bg-white/70 p-4 text-center shadow-sm">
              <div className="font-heading text-3xl font-medium text-secondary">{v}</div>
              <div className="mt-1 text-xs text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>

        {r.completed_projects.length > 0 && (
          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Projects completed</div>
            <ul className="mt-1.5 space-y-1 text-sm">
              {r.completed_projects.map((p) => <li key={p.id}>✅ {p.title}{p.students_affected ? ` — ${p.students_affected} students` : ""}</li>)}
            </ul>
          </div>
        )}
        {r.top_champions.length > 0 && (
          <div className="mt-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Top champions</div>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {r.top_champions.map((c) => <span key={c.name} className="rounded-full bg-white/70 px-2.5 py-1 text-xs">🌟 {c.name} · {c.projects} project{c.projects !== 1 ? "s" : ""}</span>)}
            </div>
          </div>
        )}
        <div className="mt-6 text-center text-[11px] italic text-muted-foreground">"Your alma mater remembers what you gave back." · alumnipulse.net</div>
      </div>
    </div>
  );
}
